/*
 * Product & line item notes
 *
 * 1. Product page "Product note" block - character counter and, when the note is
 *    required, validation before the item is added to the cart (the product form
 *    is rendered with `novalidate`, so this is handled here).
 * 2. <cart-item-note> - lets a customer add or edit the note on a single cart
 *    line item. The note is stored as a line item property so it carries through
 *    to checkout and the order.
 */

(function () {
  if (window.themeNoteFieldsLoaded) return;
  window.themeNoteFieldsLoaded = true;

  function refreshCounter(field) {
    const scope = field.closest('[data-note-scope]');
    if (!scope) return;
    const counter = scope.querySelector('[data-note-count]');
    if (counter) counter.textContent = field.value.length;
  }

  function refreshAllCounters(root) {
    (root || document).querySelectorAll('[data-note-field]').forEach(refreshCounter);
  }

  document.addEventListener('input', (event) => {
    const field = event.target.closest('[data-note-field]');
    if (!field) return;

    refreshCounter(field);

    const scope = field.closest('[data-note-scope]');
    if (scope && field.value.trim() !== '') {
      scope.classList.remove('note-field--error');
      const message = scope.querySelector('[data-note-error]');
      if (message) message.hidden = true;
    }
  });

  document.addEventListener('DOMContentLoaded', () => refreshAllCounters());
  if (document.readyState !== 'loading') refreshAllCounters();

  // Required notes. Captured on document so this runs before the product form's
  // own submit handler, which stops the event from bubbling any further.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || typeof form.matches !== 'function') return;
      if (!form.matches('form[data-type="add-to-cart-form"]')) return;

      const root = form.closest('product-info') || document;
      let firstInvalid = null;

      root.querySelectorAll('[data-note-scope][data-note-required="true"]').forEach((scope) => {
        const field = scope.querySelector('[data-note-field]');
        if (!field) return;

        const isEmpty = field.value.trim() === '';
        scope.classList.toggle('note-field--error', isEmpty);

        const message = scope.querySelector('[data-note-error]');
        if (message) message.hidden = !isEmpty;

        if (isEmpty && !firstInvalid) firstInvalid = field;
      });

      if (!firstInvalid) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const details = firstInvalid.closest('details');
      if (details) details.open = true;

      firstInvalid.focus();
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    true
  );

  if (!customElements.get('cart-item-note')) {
    customElements.define(
      'cart-item-note',
      class CartItemNote extends HTMLElement {
        connectedCallback() {
          if (this.bound) return;
          this.bound = true;

          this.field = this.querySelector('[data-note-field]');
          if (!this.field) return;

          refreshCounter(this.field);

          const saveButton = this.querySelector('[data-note-save]');
          const clearButton = this.querySelector('[data-note-clear]');

          if (saveButton) {
            saveButton.addEventListener('click', () => this.save(this.field.value));
          }

          if (clearButton) {
            clearButton.addEventListener('click', () => {
              this.field.value = '';
              refreshCounter(this.field);
              this.save('');
            });
          }
        }

        get cartItems() {
          return this.closest('cart-items') || this.closest('cart-drawer-items');
        }

        save(value) {
          const cartItems = this.cartItems;
          if (!cartItems) return;

          const line = this.dataset.index;
          const property = this.dataset.property;
          let properties;

          // The Cart API replaces the whole properties hash, so the item's
          // existing properties are sent back untouched alongside the note.
          // If they cannot be read, bail out rather than wipe them.
          try {
            properties = JSON.parse(this.dataset.properties || '{}') || {};
          } catch (error) {
            console.error('cart-item-note: could not read the line item properties', error);
            return;
          }

          // An empty value is kept rather than deleted so the property is cleared
          // whether the Cart API replaces or merges the properties hash. Shopify
          // ignores line item properties with a blank value.
          properties[property] = value.trim();

          this.setAttribute('aria-busy', 'true');
          cartItems.enableLoading(line);

          const body = JSON.stringify({
            line,
            properties,
            sections: cartItems.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname,
          });

          fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
            .then((response) => response.text())
            .then((state) => {
              const parsedState = JSON.parse(state);

              if (parsedState.errors) {
                cartItems.updateLiveRegions(line, parsedState.errors);
                return;
              }

              cartItems.getSectionsToRender().forEach((section) => {
                const container = document.getElementById(section.id);
                if (!container) return;

                const elementToReplace = container.querySelector(section.selector) || container;
                elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
              });

              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
            })
            .catch(() => {
              const errors =
                document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
              if (errors) errors.textContent = window.cartStrings.error;
            })
            .finally(() => {
              this.removeAttribute('aria-busy');
              cartItems.disableLoading(line);
            });
        }
      }
    );
  }
})();
